import React from "react";
import { Box, Typography, Button, Paper, Divider } from "@mui/material";
import styled from "styled-components";
import colors from "../../theme/colors";
// Assuming you have custom or material icons for info/bell/scale metrics
import {
  InfoOutlined,
  Notifications,
  ScaleOutlined,
} from "@mui/icons-material";

// --- Styled Components ---

const DetailCard = styled(Paper)`
  display: flex;
  flex-direction: column;
  padding: 24px;
  width: 100%;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
`;

const TopRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const TankTitle = styled(Typography)`
  && {
    font-weight: bold;
    color: #1a4fca; /* Or use your custom theme primary color link colors.primary */
    font-size: 1.15rem;
  }
`;

const ContentLayout = styled(Box)`
  display: flex;
  width: 100%;
  gap: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const Column = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionHeader = styled(Typography)`
  && {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${colors.grayDarkText || "#666666"};
    font-weight: bold;
    margin-bottom: 8px;
  }
`;

const RowItem = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const IconWrapper = styled(Box)`
  color: #b0bec5; /* Muted gray for side indicators */
  display: flex;
  align-items: center;
  margin-top: 2px;
`;

const LabelText = styled(Typography)`
  && {
    color: ${colors.grayDarkText || "#333333"};
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const ValueText = styled.span`
  font-weight: bold;
  color: #000000;
`;

const StyledVerticalDivider = styled(Divider)`
  && {
    border-color: #e0e0e0;
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

// --- Main Tank Details Component ---

export function TankDetailsWidget({
  tankName = "19_EMBRYO_TM0315",
  scaleId = "e00fce6804d6012997a897d8",
  tankContents = "--",
  tankType = "--",
  tankMake = "--",
  tankModel = "--",
  fullWeightKg = "--",
  lowWeightThresholdKg = "--",
  consumptionThreshold24h = "--",
  minExternalTempC = "--",
  manageOnClick = () => {},
}) {
  return (
    <DetailCard variant="outlined">
      {/* Upper Context Header */}
      <TopRow>
        <TankTitle>{tankName}</TankTitle>
        <Button variant="contained" onClick={manageOnClick}>
          Manage
        </Button>
      </TopRow>

      {/* Dynamic Data Split Grid */}
      <ContentLayout>
        {/* Left Specification Column */}
        <Column>
          <SectionHeader>Tank Details</SectionHeader>

          <RowItem>
            <IconWrapper>
              <ScaleOutlined fontSize="small" />
            </IconWrapper>
            <LabelText>
              Scale ID:{" "}
              <ValueText style={{ fontWeight: "normal", color: "#555" }}>
                {scaleId}
              </ValueText>
            </LabelText>
          </RowItem>

          <RowItem>
            <IconWrapper>
              <InfoOutlined fontSize="small" />
            </IconWrapper>
            <LabelText>
              Tank Contents: <ValueText>{tankContents}</ValueText>
            </LabelText>
          </RowItem>

          <RowItem>
            <IconWrapper>
              <InfoOutlined fontSize="small" />
            </IconWrapper>
            <LabelText>
              Tank Type: <ValueText>{tankType}</ValueText>
            </LabelText>
          </RowItem>

          <RowItem>
            <IconWrapper>
              <InfoOutlined fontSize="small" />
            </IconWrapper>
            <LabelText>
              Tank Make: <ValueText>{tankMake}</ValueText>
            </LabelText>
          </RowItem>

          <RowItem>
            <IconWrapper>
              <InfoOutlined fontSize="small" />
            </IconWrapper>
            <LabelText>
              Tank Model: <ValueText>{tankModel}</ValueText>
            </LabelText>
          </RowItem>
        </Column>

        {/* Structural Column Divider */}
        <StyledVerticalDivider orientation="vertical" flexItem />

        {/* Right Threshold Alerting Column */}
        <Column>
          <SectionHeader>Alarm Settings</SectionHeader>

          <RowItem>
            <IconWrapper>
              <Notifications fontSize="small" />
            </IconWrapper>
            <LabelText>
              Full Weight: <ValueText>{fullWeightKg} kg</ValueText>
            </LabelText>
          </RowItem>

          <RowItem>
            <IconWrapper>
              <Notifications fontSize="small" />
            </IconWrapper>
            <LabelText>
              Low Weight Threshold:{" "}
              <ValueText>{lowWeightThresholdKg} kg</ValueText>
            </LabelText>
          </RowItem>

          <RowItem>
            <IconWrapper>
              <Notifications fontSize="small" />
            </IconWrapper>
            <LabelText>
              High Evaporation Rate Threshold:{" "}
              <ValueText>{consumptionThreshold24h} kg/24hr</ValueText>
            </LabelText>
          </RowItem>

          <RowItem>
            <IconWrapper>
              <Notifications fontSize="small" />
            </IconWrapper>
            <LabelText>
              Minimum External Temperature:{" "}
              <ValueText>{minExternalTempC}°C</ValueText>
            </LabelText>
          </RowItem>
        </Column>
      </ContentLayout>
    </DetailCard>
  );
}

export default TankDetailsWidget;
